import { uniq } from "lodash";
import type { ProductionSegmentType, ShiftSegment } from "../types/types";

export const mergeSegments = (segments1, segments2): any[] => {
  const nonce = Date.now();
  return [...segments1, ...segments2.map(s => ({ ...s, nonce }))]
    .sort((a, b) => {
      // Sort the segments first by ascending start time of the segment, and
      // then by descending nonce (so the most recent segment will be first).
      // Later we then keep only the first segment.
      if (a.range.ge && b.range.ge) {
        const d = a.range.ge - b.range.ge;
        if (d !== 0) {
          return d;
        }
        return (b.nonce || 0) - (a.nonce || 0);
      } else if (a.range.ge) {
        return 1;
      } else if (b.range.ge) {
        return -1;
      }
      // The case where they are both null
      return (b.nonce || 0) - (a.nonce || 0);
    })
    .reduce((a, s) => {
      // Always let the first segment through
      const alen = a.length;
      if (alen === 0) {
        return [s];
      }
      const lastSeg = a[alen - 1];
      // If the last segment already has a null lt, or if the last
      // segment overlaps the current segment, we need to check
      // if the current segment has a more recent nonce. If it does, we
      // need to replace the last segment with the current segment.
      if (!lastSeg.range.lt || lastSeg.range.lt > s.range.ge) {
        if (s.nonce > lastSeg.nonce) {
          return [...a.slice(0, -1), s];
        } else {
          return a;
        }
      }
      return [...a, s];
    }, []);
};

/**  https://app.hubspot.com/contacts/19900759/record/0-5/3079388084/
 * - bug: just after shift change, Askr sometimes returns a shift-long unknown segment spanning the entire following shift, causing ProcessTile to stay unknown during the entire shift
 * - patch: if a segment is unknown, ends in future, and spans an entire shift, ignore it
 */
export const patchShiftLongUnknownSegmentBug = (
  segments: ProductionSegmentType[],
  todaysShifts: ShiftSegment[]
) => {
  const todaysShiftsDurations = uniq(
    todaysShifts.map(shift => shift.range.lt - shift.range.ge)
  );
  const now = Date.now();
  return segments.filter((segment: ProductionSegmentType) => {
    if (!segment.range.ge || !segment.range.lt) {
      return true;
    }
    if (segment.pnl.category !== "unknown") {
      return true;
    }
    if (segment.range.lt <= now) {
      return true;
    }
    // if the segment duration matches any of todaysShifts durations, filter it out (usually shift have identical durations but not always)
    if (todaysShiftsDurations.includes(segment.range.lt - segment.range.ge)) {
      return false;
    }
    return true;
  });
};

// TECHDEBT: Make this work with open ended ranges
export const findGaps = (segments, ge: number, lt: number) => {
  if (segments.length === 0) {
    return [{ ge, lt }];
  }

  const gaps = [] as { ge: number; lt: number }[];
  let token = Math.min(ge, +segments[0].range.ge);
  // We will push a token along, checking for gaps between the token
  // and the start of the segment at each step, while only considering
  // segments intersecting the [ge, lt) range.
  for (const { range } of segments) {
    if (range.ge && +range.ge > token && +range.ge > ge) {
      // If there is a gap between the start of the seg
      gaps.push({ ge: +token, lt: +range.ge });
    }
    token = +range.lt; // Place token at end of latest segment
    if (!token || token >= lt) {
      // In the case where the token is open on the right, we know
      // there can't be any more gaps. If the token has been
      // pushed past the range, we don't care about those gaps.
      break;
    }
  }

  // Need to check that there isn't a gap at the end
  if (token && token < lt) {
    gaps.push({ ge: +token, lt: +lt });
  }

  return gaps;
};

export const findIntersecting = (
  segments: ProductionSegmentType[],
  t0: number,
  t1?: number
) => {
  // TECHDEBT: First do something like a binary search to trim the segments array
  return segments.filter(({ range }) => {
    const ge = range.ge && Number(range.ge);
    const lt = range.lt && Number(range.lt);
    return (
      // ge                      lt
      // <------------------------>
      //            t0
      //            t0------
      (ge === null && lt === null) ||
      // ge         lt
      // <-----------|
      //            t0
      //            t0------
      (ge === null && lt && t0 < lt) ||
      //     ge                  lt
      //     |-------------------->
      //     t0
      //     t0------
      (ge && ge <= t0 && lt === null) ||
      //     ge                  lt
      //     |-------------------->
      //  ------t1
      (t1 !== undefined && ge && ge <= t1 && lt === null) ||
      //      ge              lt
      //      |----------------|
      //            t0
      //            t0------------
      (ge && lt && ge <= t0 && t0 < lt) ||
      //        ge            lt
      //        |--------------|
      //    ------------t1
      (t1 !== undefined && ge && lt && ge <= t1 && t1 < lt) ||
      //        ge            lt
      //        |--------------|
      //    t0---------------------t1
      (t1 !== undefined && ge && lt && t0 <= ge && t1 >= lt)
    );
  });
};
